import React from 'react';

import PropTypes from 'prop-types';

import { TOOL_IDS } from '@adsk/alloy-provider';
import { DocumentTextIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import { TabTitle } from '@adsk/exo-skeleton-react';

import DocsActivityCard from './DocsActivityCard';
import CustomDataView from './DocsCustomDataView';
import DocsCustomTopBanner from './DocsCustomTopBanner';
import EntityBlock from './DocsEntityBlock';
import EntityMenuItem from './DocsEntityMenuItem';
import { FOLDER_LIMIT, DOCUMENT_LIMIT } from './helpers/consts';
import { formatEntityBlock, formatEntityItem } from './helpers/formatEntities';
import Services, { LocaleUtil } from './helpers/sdkServices';
import { getTopFolderUrn } from './helpers/searchHelper';
import { BOOTSTRAP_NODE } from './helpers/tree';
import { chunk, getDistributedAndSortedFolders, uniq } from './helpers/utils';
import UploadPanel from './UploadPanel';

const TopBarActions = () => {
  const { locale } = useLocale(({ locale }) =>
    require(`./i18n/${locale}.json`),
  );

  // When initialize show the picker modal, setup locale into SDK for folder name localization
  React.useEffect(() => {
    LocaleUtil.setLocale(locale);
  }, [locale]);

  // This is to use an empty TopBarActions component to initialized SDK required `locale` and `feature flag` info since
  // there is no other way to setup them before the plugin start to load data from SDK.
  return null;
};

const PickerModalTitle = ({ showTabTitle = true }) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const value = T.translate('ADD_DOCUMENT');

  return (
    <>
      {showTabTitle && <TabTitle value={value} />}
      {value}
    </>
  );
};

PickerModalTitle.propTypes = {
  showTabTitle: PropTypes.bool,
};

const createDocsPlugin = (
  defaultExpandedFolderUrn,
  enableFolderSearching = false,
) => {
  const docsPlugin = {
    metadata: {
      domain: 'autodesk-bim360-documentmanagement',
      type: 'documentlineage',
    },
    is3LOSupported: true,
    renderIcon: (p) => <DocumentTextIcon {...p} />,
    toolId: TOOL_IDS.DOCUMENTS,
    showSearchBox: true,
    modalProps: {
      width: 800,
      height: 700,
    },
    PluralText: ({ lowerCase }) => {
      const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
      const result = T.translate('TITLE');
      if (lowerCase && typeof result === 'string') {
        return result.toLowerCase();
      }
      return result;
    },
    PickerModalTitle,
    TopBarActions,
    getCanCreateArgs: ({ createFormProps }) => ({
      parentFolderUrn: createFormProps?.parentFolderUrn,
    }),
    canCreate: ({ parentFolderUrn }) =>
      Promise.resolve(parentFolderUrn ? true : false),
    CreateNewButtonText: () => {
      const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
      return T.translate('UPLOAD_NEW');
    },
    CreateNewEntityPanel: UploadPanel,
    AddRelationshipsButtonText: ({ count }) => {
      const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
      return T.translate('ADD_BUTTON', { context: count });
    },
    SelectedCount: ({ count }) => {
      const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
      return T.translate('SELECTED_COUNT', { context: count });
    },
    queryEntities: async ({
      projectId,
      folderUrn,
      pageNumber = 1,
      filters = {},
    }) => {
      const isSearchMode = !!filters.searchExpression;
      if (isSearchMode) {
        let queryUrn = folderUrn;
        // refetch top folder urn if empty
        if (!queryUrn) queryUrn = await getTopFolderUrn(projectId);
        if (!queryUrn) return { data: [] };

        // Call search service to get the target document
        const { documents, folders, pagination } =
          await Services.Search.proxySearch(projectId, {
            searchText: filters.searchExpression || '',
            folderUrns: queryUrn,
            sort: [{ field: 'name', order: 'asc' }],
            pageNumber,
            recursive: true,
            includeContent: false,
            includeFolders: enableFolderSearching,
            includeAttributes: false,
            includePermission: false,
            includeTopFolders: enableFolderSearching,
          });

        // Whether has next page
        const hasNext = pagination.totalPages > pageNumber;
        // Search has more data and previous batch are all deleted, it should auto fetch next batch
        const hasMoreData = enableFolderSearching
          ? documents.length === 0 && folders.length === 0
          : documents.length === 0;

        if (hasNext && hasMoreData) {
          const nextBatch = await docsPlugin.queryEntities({
            projectId,
            folderUrn: queryUrn,
            filters,
            pageNumber: pageNumber + 1,
          });
          return nextBatch;
        }

        return {
          data: enableFolderSearching
            ? [
                ...getDistributedAndSortedFolders(folders).map(
                  formatEntityItem,
                ),
                ...documents.map(formatEntityItem),
              ]
            : documents.map(formatEntityItem),
          ...(hasNext && {
            nextPageQuery: {
              projectId,
              folderUrn: queryUrn,
              filters,
              pageNumber: pageNumber + 1,
            },
          }),
        };
      } else {
        return {
          data: [BOOTSTRAP_NODE],
        };
      }
    },

    getEntitiesByIds: async ({ ids, projectId }) => {
      try {
        const chunkIds = chunk(ids, DOCUMENT_LIMIT);
        const chunks = await Promise.all(
          chunkIds.map((ids) => Services.Projects.getDocuments(projectId, ids)),
        );
        const documents = chunks.flat();
        let folders = [];

        try {
          const folderUrns = uniq(
            documents.map(({ parentFolderUrn }) => parentFolderUrn),
          );
          const chunksUrns = chunk(folderUrns, FOLDER_LIMIT);
          const res = await Promise.all(
            chunksUrns.map((urns) =>
              Services.Folders.getFoldersByUrns(
                projectId,
                {},
                {
                  urns,
                },
              ),
            ),
          );
          folders = res.map(({ results }) => results).flat();
        } catch (_) {
          /* empty */
        }

        const data = documents.map((entity) => {
          entity = formatEntityBlock(entity);
          const parentFolder = folders.find(
            (folder) => folder.urn === entity.parentFolderUrn,
          );
          parentFolder && (entity.parentFolder = parentFolder);

          return entity;
        });
        return { data };
      } catch (e) {
        return { data: [] };
      }
    },
    EntityBlock,
    CustomDataView,
    EntityMenuItem,
    ActivityCard: DocsActivityCard,
    getDefaultExpandedFolderUrn: () => defaultExpandedFolderUrn,
    CustomTopBanner: DocsCustomTopBanner,
  };

  return docsPlugin;
};

export const createPlugin = ({
  onEntityBlockClick, // a customized click handler for clicking on EntityBlock, optional, shape (entity) => *
  defaultExpandedFolderUrn,
  enableFolderSearching,
} = {}) => {
  const docsPlugin = createDocsPlugin(
    defaultExpandedFolderUrn,
    enableFolderSearching,
  );

  return {
    ...docsPlugin,
    PickerModalTitle: (props) => <PickerModalTitle {...props} />,
    EntityBlock: (props) => (
      <EntityBlock {...props} onClick={onEntityBlockClick} />
    ),
  };
};

export default createDocsPlugin();
